import React, { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { DocumentTextIcon, KeyIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import ReusableButton from './ReusableButton';
import * as precheckService from '../../services/precheckService';

interface SinglePrecheckViewProps<T> {
  type: 'precheck' | 'precheck-pos';
  data: T | null;
  loading: boolean;
  error: string | null;
  getTitle: (data: T) => string;
  renderContent: (data: T) => ReactNode;
}

function SinglePrecheckView<T extends { id: number } & precheckService.SubmissionSource>({
  type,
  data,
  loading,
  error,
  getTitle,
  renderContent,
}: SinglePrecheckViewProps<T>) {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const handleDownloadPdf = async () => {
    if (!id) return;

    const currentLanguage = i18n.language || 'en';
    try {
      const blob = await precheckService.getPdf(type, parseInt(id), currentLanguage, true);
      precheckService.openPdfInNewTab(blob);
    } catch (error) {
      console.error('Error fetching PDF:', error);
    }
  };

  return (
    <div className="container">
      <style>
        {`
          table td:first-child {
            width: 30%;
          }
        `}
      </style>

      <h2 className="text-xl font-bold mb-4">
        {data ? getTitle(data) : '-'}
      </h2>

      {data?.channel === 'api' && (
        <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm text-indigo-900">
          <span className="inline-flex items-center gap-1.5 font-semibold">
            <KeyIcon className="size-4" aria-hidden="true" />
            {t('apiKeys.submittedViaApi')}
          </span>
          <span>
            {t('apiKeys.apiKey')}:{' '}
            {data.apiKey ? `${data.apiKey.name} (${data.apiKey.keyPrefix}…)` : t('apiKeys.deletedKey')}
          </span>
          {data.externalReference && (
            <span>
              {t('apiKeys.reference')}: <span className="font-mono">{data.externalReference}</span>
            </span>
          )}
        </div>
      )}

      {loading && <p>{t('common.loading')}</p>}
      {error && (
        <p>
          {t('common.error')}: {error}
        </p>
      )}

      <ReusableButton
        onClick={handleDownloadPdf}
        icon={<DocumentTextIcon className="size-5" aria-hidden="true" />}
        label={t('common.viewPDF')}
      />

      {data && renderContent(data)}
    </div>
  );
}

export default SinglePrecheckView;
